/**
 * Publish Modal Component
 * 
 * Modal for publishing projects with subdomain selection,
 * visibility settings (public/password), and status display.
 * 
 * Similar to Lovable's publish popup but with our styling.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { cloudProjectApi } from '../services/cloudApi';
import { overlayDismissProps } from '../utils/overlayDismiss';
import './PublishModal.css';

// Icons
const GlobeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
);

const LockIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
);

const LinkIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
);

const CopyIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
);

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const ExternalLinkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
);

const RefreshIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 4 23 10 17 10"/>
        <polyline points="1 20 1 14 7 14"/>
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
    </svg>
);

const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

const LoadingSpinner = () => (
    <div className="publish-spinner">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
            <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round">
                <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
            </path>
        </svg>
    </div>
);

const PUBLISH_DOMAIN = process.env.REACT_APP_PUBLISH_DOMAIN || 'localtest.me';
const PUBLISH_SCHEME = process.env.REACT_APP_PUBLISH_SCHEME || (PUBLISH_DOMAIN === 'localtest.me' ? 'http' : 'https');

export function PublishModal({ projectId, projectName, isOpen, onClose, initialData }) {
    // State
    const [subdomain, setSubdomain] = useState('');
    const [subdomainError, setSubdomainError] = useState('');
    const [subdomainAvailable, setSubdomainAvailable] = useState(null);
    const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
    
    const [visibility, setVisibility] = useState('PUBLIC');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    const [isPublishing, setIsPublishing] = useState(false);
    const [isPublished, setIsPublished] = useState(false);
    const [publishStatus, setPublishStatus] = useState(null); // QUEUED, BUILDING, DEPLOYED, FAILED
    const [publishProgress, setPublishProgress] = useState(0);
    const [publishError, setPublishError] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    
    const [copied, setCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // Reset state when modal closes or projectId changes
    useEffect(() => {
        if (!isOpen) {
            // Reset state when modal closes
            setSubdomain('');
            setSubdomainError('');
            setSubdomainAvailable(null);
            setVisibility('PUBLIC');
            setPassword('');
            setShowPassword(false);
            setIsPublishing(false);
            setIsPublished(false);
            setPublishStatus(null);
            setPublishProgress(0);
            setPublishError('');
            setPreviewUrl('');
            setCopied(false);
            setIsLoading(false);
        }
    }, [isOpen]);
    
    // Fetch current publish status when modal opens
    useEffect(() => {
        if (!isOpen || !projectId) return;
        
        const fetchPublishStatus = async () => {
            setIsLoading(true);
            try {
                const status = await cloudProjectApi.getPublishStatus(projectId);
                if (status && status.is_published) {
                    setIsPublished(true);
                    setPublishStatus('DEPLOYED');
                    if (status.subdomain) setSubdomain(status.subdomain);
                    if (status.visibility) setVisibility(status.visibility);
                    if (status.preview_url) setPreviewUrl(status.preview_url);
                    // Mark as already having a subdomain so we don't re-check availability
                    setSubdomainAvailable(true);
                } else {
                    // Has deployment but not published (maybe failed or in progress)
                    if (status.subdomain) setSubdomain(status.subdomain);
                    if (status.status) setPublishStatus(status.status);
                    generateSubdomainIfNeeded();
                }
            } catch (err) {
                // No existing publish, use initialData or generate subdomain
                if (initialData?.is_published) {
                    setIsPublished(true);
                    setPublishStatus('DEPLOYED');
                    if (initialData.subdomain) setSubdomain(initialData.subdomain);
                    if (initialData.visibility) setVisibility(initialData.visibility);
                    if (initialData.preview_url) setPreviewUrl(initialData.preview_url);
                } else {
                    generateSubdomainIfNeeded();
                }
            } finally {
                setIsLoading(false);
            }
        };
        
        const generateSubdomainIfNeeded = () => {
            if (projectName) {
                const generated = projectName
                    .toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')
                    .replace(/[\s_]+/g, '-')
                    .replace(/-+/g, '-')
                    .trim()
                    .slice(0, 40);
                setSubdomain(generated || `project-${projectId?.slice(0, 8)}`);
            }
        };
        
        fetchPublishStatus();
    }, [isOpen, projectId, projectName, initialData]);
    
    // Check subdomain availability with debounce
    const checkSubdomain = useCallback(async (value, currentProjectId) => {
        if (!value || value.length < 4) {
            setSubdomainError('Subdomain must be at least 4 characters');
            setSubdomainAvailable(null);
            return;
        }
        
        if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(value)) {
            setSubdomainError('Only lowercase letters, numbers, and hyphens allowed');
            setSubdomainAvailable(null);
            return;
        }
        
        setIsCheckingSubdomain(true);
        setSubdomainError('');
        
        try {
            // Pass projectId so republishing with same subdomain is allowed
            const result = await cloudProjectApi.checkSubdomain(value, currentProjectId);
            setSubdomainAvailable(result.available);
            if (!result.available && result.suggestion) {
                setSubdomainError(`Not available. Try: ${result.suggestion}`);
            }
        } catch (err) {
            setSubdomainError('Failed to check availability');
        } finally {
            setIsCheckingSubdomain(false);
        }
    }, []);
    
    // Debounced subdomain check
    useEffect(() => {
        const timer = setTimeout(() => {
            // Always check availability, passing projectId to allow same subdomain for republish
            if (subdomain) {
                checkSubdomain(subdomain, projectId);
            }
        }, 500);
        
        return () => clearTimeout(timer);
    }, [subdomain, checkSubdomain, projectId]);
    
    // Poll for publish status
    useEffect(() => {
        let pollInterval;
        
        if (isPublishing && publishStatus && ['QUEUED', 'BUILDING'].includes(publishStatus)) {
            pollInterval = setInterval(async () => {
                try {
                    const status = await cloudProjectApi.getPublishStatus(projectId);
                    setPublishStatus(status.status);
                    
                    if (status.status === 'DEPLOYED') {
                        setIsPublished(true);
                        setIsPublishing(false);
                        setPreviewUrl(status.preview_url);
                        setPublishProgress(100);
                    } else if (status.status === 'FAILED') {
                        setIsPublishing(false);
                        setPublishError(status.error_message || 'Publish failed');
                        setPublishProgress(0);
                    } else if (status.build_logs) {
                        // Parse progress from logs
                        const match = status.build_logs.match(/Progress: (\d+)%/);
                        if (match) {
                            setPublishProgress(parseInt(match[1]));
                        }
                    }
                } catch (err) {
                    console.error('Failed to poll status:', err);
                }
            }, 2000);
        }
        
        return () => {
            if (pollInterval) clearInterval(pollInterval);
        };
    }, [isPublishing, publishStatus, projectId]);
    
    // Handle publish
    const handlePublish = async () => {
        if (visibility === 'PASSWORD' && (!password || password.length < 4)) {
            setPublishError('Password must be at least 4 characters');
            return;
        }
        
        setIsPublishing(true);
        setPublishError('');
        setPublishProgress(5);
        setPublishStatus('QUEUED');
        
        try {
            const result = await cloudProjectApi.publish(projectId, {
                subdomain: subdomain,
                visibility: visibility,
                password: visibility === 'PASSWORD' ? password : undefined
            });
            
            setPublishStatus(result.status);
            if (result.preview_url) setPreviewUrl(result.preview_url);
            
            if (result.status === 'DEPLOYED') {
                setIsPublished(true);
                setIsPublishing(false);
                setPublishProgress(100);
            }
        } catch (err) {
            setIsPublishing(false);
            setPublishError(err.message || 'Failed to publish');
            setPublishStatus('FAILED');
            setPublishProgress(0);
        }
    };
    
    // Handle republish
    const handleRepublish = async () => {
        setIsPublishing(true);
        setPublishError('');
        setPublishProgress(5);
        setPublishStatus('QUEUED');
        
        try {
            const result = await cloudProjectApi.publish(projectId, {
                subdomain: subdomain,
                visibility: visibility,
                password: visibility === 'PASSWORD' ? password : undefined
            });
            
            setPublishStatus(result.status);
            if (result.preview_url) setPreviewUrl(result.preview_url);
        } catch (err) {
            setIsPublishing(false);
            setPublishError(err.message || 'Failed to republish');
            setPublishStatus('FAILED');
            setPublishProgress(0);
        }
    };
    
    // Handle unpublish
    const handleUnpublish = async () => {
        if (!window.confirm('Are you sure you want to unpublish this project? The URL will no longer be accessible.')) {
            return;
        }
        
        try {
            await cloudProjectApi.unpublish(projectId);
            setIsPublished(false);
            setPublishStatus(null);
            setPreviewUrl('');
        } catch (err) {
            setPublishError(err.message || 'Failed to unpublish');
        }
    };
    
    // Copy URL to clipboard
    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(previewUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    
    // Open URL in new tab
    const handleOpenUrl = () => {
        window.open(previewUrl, '_blank');
    };
    
    if (!isOpen) return null;
    
    const fullUrl = `${PUBLISH_SCHEME}://${subdomain}.${PUBLISH_DOMAIN}`;
    
    return (
        <div className="publish-modal-overlay" {...overlayDismissProps(onClose)}>
            <div className="publish-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="publish-modal-header">
                    <h2>Publish to Web</h2>
                    <button className="publish-close-btn" onClick={onClose}>
                        <CloseIcon />
                    </button>
                </div>
                
                {/* Content */}
                <div className="publish-modal-content">
                    {/* Loading State */}
                    {isLoading && (
                        <div className="publish-loading">
                            <LoadingSpinner />
                            <span>Loading publish status...</span>
                        </div>
                    )}
                    
                    {/* Status Badge */}
                    {!isLoading && isPublished && !isPublishing && (
                        <div className="publish-status-badge live">
                            <span className="status-dot"></span>
                            Live
                        </div>
                    )}
                    
                    {/* URL Input */}
                    <div className="publish-field">
                        <label>Published URL</label>
                        <div className="publish-url-input">
                            <span className="url-prefix">{PUBLISH_SCHEME}://</span>
                            <input
                                type="text"
                                value={subdomain}
                                onChange={e => {
                                    const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                                    setSubdomain(val);
                                    setSubdomainAvailable(null);
                                }}
                                placeholder="your-site-name"
                                disabled={isPublishing}
                            />
                            <span className="url-suffix">.{PUBLISH_DOMAIN}</span>
                            {isCheckingSubdomain && <LoadingSpinner />}
                            {!isCheckingSubdomain && subdomainAvailable === true && (
                                <span className="subdomain-available"><CheckIcon /></span>
                            )}
                        </div>
                        {subdomainError && (
                            <p className="publish-field-error">{subdomainError}</p>
                        )}
                    </div>
                    
                    {/* Visibility Options */}
                    <div className="publish-field">
                        <label>Who can visit the URL?</label>
                        <div className="publish-visibility-options">
                            <button
                                className={`visibility-option ${visibility === 'PUBLIC' ? 'active' : ''}`}
                                onClick={() => setVisibility('PUBLIC')}
                                disabled={isPublishing}
                            >
                                <GlobeIcon />
                                <div className="visibility-text">
                                    <span className="visibility-title">Public</span>
                                    <span className="visibility-desc">Anyone with the link</span>
                                </div>
                            </button>
                            <button
                                className={`visibility-option ${visibility === 'PASSWORD' ? 'active' : ''}`}
                                onClick={() => setVisibility('PASSWORD')}
                                disabled={isPublishing}
                            >
                                <LockIcon />
                                <div className="visibility-text">
                                    <span className="visibility-title">Password</span>
                                    <span className="visibility-desc">Requires a password</span>
                                </div>
                            </button>
                        </div>
                    </div>
                    
                    {/* Password Input (if password protected) */}
                    {visibility === 'PASSWORD' && (
                        <div className="publish-field">
                            <label>Site Password</label>
                            <div className="publish-password-input">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Enter password (min 4 characters)"
                                    disabled={isPublishing}
                                />
                                <button
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    type="button"
                                >
                                    {showPassword ? 'Hide' : 'Show'}
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {/* Publishing Progress */}
                    {isPublishing && (
                        <div className="publish-progress">
                            <div className="progress-header">
                                <span className="progress-status">
                                    {publishStatus === 'QUEUED' && 'Preparing...'}
                                    {publishStatus === 'BUILDING' && 'Building project...'}
                                </span>
                                <span className="progress-percent">{publishProgress}%</span>
                            </div>
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill" 
                                    style={{ width: `${publishProgress}%` }}
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* Error Message */}
                    {publishError && (
                        <div className="publish-error">
                            <p>{publishError}</p>
                        </div>
                    )}
                    
                    {/* Published URL Actions */}
                    {isPublished && previewUrl && !isPublishing && (
                        <div className="publish-live-section">
                            <div className="publish-live-url">
                                <LinkIcon />
                                <span className="live-url-text">{previewUrl}</span>
                                <button 
                                    className="url-action-btn" 
                                    onClick={handleCopyUrl}
                                    title="Copy URL"
                                >
                                    {copied ? <CheckIcon /> : <CopyIcon />}
                                </button>
                                <button 
                                    className="url-action-btn" 
                                    onClick={handleOpenUrl}
                                    title="Open in new tab"
                                >
                                    <ExternalLinkIcon />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div className="publish-modal-footer">
                    {!isPublished ? (
                        <button
                            className="publish-btn primary"
                            onClick={handlePublish}
                            disabled={isPublishing || !subdomain || subdomainAvailable === false}
                        >
                            {isPublishing ? (
                                <>
                                    <LoadingSpinner />
                                    Publishing...
                                </>
                            ) : (
                                'Publish'
                            )}
                        </button>
                    ) : (
                        <div className="publish-actions">
                            <button
                                className="publish-btn secondary"
                                onClick={handleUnpublish}
                                disabled={isPublishing}
                            >
                                <TrashIcon />
                                Unpublish
                            </button>
                            <button
                                className="publish-btn primary"
                                onClick={handleRepublish}
                                disabled={isPublishing}
                            >
                                {isPublishing ? (
                                    <>
                                        <LoadingSpinner />
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <RefreshIcon />
                                        Update
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PublishModal;

